import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { useLocation, useNavigate } from "react-router";
import { SocialHeader } from '@edusense/shared-ui-layout';
import Tooltip from '@edusense/shared-ui-kit/atoms/Tooltip';
import { useGetCourseDetail } from '@edusense/course-client';
import { useDisclosure } from '@heroui/react';
import { InfoModal, ReportDialog } from '@edusense/shared-ui-components';
// Lucide React 직접 import - Vite 번들링 최적화 및 초기화 순서 문제 해결
import { Info } from 'lucide-react';
import { Star } from 'lucide-react';
import { Users } from 'lucide-react';
import { CheckCircle } from 'lucide-react';
import { Gift } from 'lucide-react';
import { Award } from 'lucide-react';
import { Globe } from 'lucide-react';
import { Edit } from 'lucide-react';
import { Trash2 } from 'lucide-react';
import { MinusCircle } from 'lucide-react';
import { CheckCircle2 } from 'lucide-react';
import { AlertCircle } from 'lucide-react';
import { Flag } from 'lucide-react';
// 스타일 import
import { CardContainer, CardContent, HeaderWrapper, BodyWrapper, TitleWrapper, Title, Description, MediaGridWrapper, FooterRow, LeftFooter, RightFooter, ThumbnailImage } from './styles';
import { RatingWrapper, StarIconWrapper, LevelIndicator, LevelInfoIconWrapper, EnrollmentWrapper, UsersIconWrapper, ProgressWrapper, CompletedBadge, CompletedIconWrapper, EnrolledBadge, EnrolledIconWrapper, PriceWrapper, FreeBadge, GiftIconWrapper, LanguageSupport, GlobeIconWrapper, LanguageFlags, LanguageBadge, MoreLanguagesBadge, InstructorBadge, InstructorIconWrapper, PublishStatusBadge, PublishStatusIconWrapper } from './badge-styles';
// 유틸리티 함수 import
import { resolveThumbnailUrl, translateLevel, getLanguageName, getLevelDescription, formatPrice, parseRating, parseEnrollmentCount, parseReviewCount, normalizeLanguageCode, getLanguageBadgeLabel } from './utils';
const collectLanguages = (defaultLanguage, languages)=>{
    const normalized = new Set();
    const normalizedDefault = normalizeLanguageCode(defaultLanguage);
    if (normalizedDefault) {
        normalized.add(normalizedDefault);
    }
    (languages ?? []).forEach((languageCode)=>{
        const normalizedCode = normalizeLanguageCode(languageCode);
        if (normalizedCode) {
            normalized.add(normalizedCode);
        }
    });
    return Array.from(normalized);
};
const CourseCard = ({ course, isEnrolled = false, isInstructor = false, onCardClick, onAuthorClick, onEdit, onDelete, onUnpublish, onReport, isDraft = false })=>{
    const location = useLocation();
    const navigate = useNavigate();
    const { isOpen: isLanguageModalOpen, onOpen: onLanguageModalOpen, onClose: onLanguageModalClose } = useDisclosure();
    const { isOpen: isReportModalOpen, onOpen: onReportModalOpen, onClose: onReportModalClose } = useDisclosure();
    const courseLevelLanguages = useMemo(()=>collectLanguages(course.defaultLanguage, course.availableLanguages), [
        course.availableLanguages,
        course.defaultLanguage
    ]);
    const shouldFetchDetailLanguages = courseLevelLanguages.length <= 1;
    const { data: detailData } = useGetCourseDetail(course.id, undefined, {
        query: {
            enabled: shouldFetchDetailLanguages,
            staleTime: 1000 * 60 * 5,
            gcTime: 1000 * 60 * 30,
            retry: 0
        }
    });
    const detailLanguages = useMemo(()=>collectLanguages(detailData?.summary?.defaultLanguage, detailData?.summary?.supportedLanguages), [
        detailData?.summary?.defaultLanguage,
        detailData?.summary?.supportedLanguages
    ]);
    const languagesForDisplay = courseLevelLanguages.length > 1 ? courseLevelLanguages : detailLanguages;
    // 언어 지원 UI 렌더링 함수
    const renderLanguageSupport = ()=>{
        if (languagesForDisplay.length <= 1) {
            return null;
        }
        const visibleLanguages = languagesForDisplay.slice(0, 2);
        const hiddenLanguages = languagesForDisplay.slice(2);
        const fullLabel = languagesForDisplay.map(getLanguageName).join(', ');
        return /*#__PURE__*/ _jsx(Tooltip, {
            content: `지원 언어: ${fullLabel}`,
            position: "top",
            delay: 200,
            children: /*#__PURE__*/ _jsxs(LanguageSupport, {
                children: [
                    /*#__PURE__*/ _jsx(GlobeIconWrapper, {
                        children: /*#__PURE__*/ _jsx(Globe, {})
                    }),
                    /*#__PURE__*/ _jsxs(LanguageFlags, {
                        children: [
                            visibleLanguages.map((language)=>/*#__PURE__*/ _jsx(LanguageBadge, {
                                    children: getLanguageBadgeLabel(language)
                                }, language)),
                            hiddenLanguages.length > 0 && /*#__PURE__*/ _jsx(Tooltip, {
                                content: hiddenLanguages.map(getLanguageName).join(', '),
                                position: "top",
                                delay: 150,
                                children: /*#__PURE__*/ _jsxs(MoreLanguagesBadge, {
                                    children: [
                                        "+",
                                        hiddenLanguages.length
                                    ]
                                })
                            })
                        ]
                    })
                ]
            })
        });
    };
    // 가격 표시 렌더링 함수
    const renderPrice = ()=>{
        if (course.isFree) {
            return /*#__PURE__*/ _jsxs(FreeBadge, {
                children: [
                    /*#__PURE__*/ _jsx(GiftIconWrapper, {
                        children: /*#__PURE__*/ _jsx(Gift, {})
                    }),
                    "무료"
                ]
            });
        }
        if (course.price?.amount !== undefined) {
            return /*#__PURE__*/ _jsx(PriceWrapper, {
                children: formatPrice(course.price)
            });
        }
        return null;
    };
    // 카드 클릭 핸들러
    const handleCardClick = ()=>{
        if (course && course.id) {
            const currentPath = location.pathname;
            console.log(`[CourseCard] Card clicked. Current path: ${currentPath}`);
            // 외부에서 전달된 핸들러 실행
            if (onCardClick) {
                onCardClick(course.id);
                return;
            }
            const segments = currentPath.split('/').filter(Boolean);
            const localeSegment = segments.find((segment)=>/^[a-z]{2}(?:-[A-Z]{2})?$/.test(segment));
            const basePath = localeSegment ? `/${localeSegment}` : '';
            const destination = `${basePath}/course/${course.id}`;
            navigate(destination);
        }
    };
    // 작성자 클릭 핸들러
    const handleAuthorClick = (author)=>{
        if (onAuthorClick) {
            onAuthorClick(author.id);
            return;
        }
        // 기본 동작: 프로필 페이지로 이동 (username 사용)
        const currentPath = location.pathname;
        const segments = currentPath.split('/').filter(Boolean);
        const localeSegment = segments.find((segment)=>/^[a-z]{2}(?:-[A-Z]{2})?$/.test(segment));
        const basePath = localeSegment ? `/${localeSegment}` : '';
        const destination = `${basePath}/profile/${author.username || author.id}`;
        navigate(destination);
    };
    // 신고 제출 핸들러
    const handleReportSubmit = async (data)=>{
        if (onReport) {
            await onReport(course.id, data);
        }
    };
    // 코스 데이터 파싱
    const { id, title, description, instructor, averageRating, enrollmentCount, progressRate, level, createdAt, isCompleted, mediaFiles } = course;
    const thumbnailUrl = resolveThumbnailUrl(course.thumbnailUrl, mediaFiles);
    const ratingValue = parseRating(averageRating);
    const enrollCountValue = parseEnrollmentCount(enrollmentCount);
    const reviewCountValue = parseReviewCount(course.reviewCount);
    const hasThumbnail = !!thumbnailUrl;
    // 작성자 정보 구성
    const author = instructor ? {
        id: instructor.id,
        name: instructor.displayName || instructor.username || '',
        username: instructor.username || '',
        profileImageUrl: instructor.profileImageUrl,
        verified: instructor.verified ?? false
    } : null;
    // 강사용 액션 메뉴 아이템 구성
    const actionMenuItems = useMemo(()=>{
        const items = [];
        // 강사 전용 액션
        if (isInstructor) {
            // 편집 버튼 (초안, 발행됨 모두)
            if (onEdit) {
                items.push({
                    label: '편집',
                    icon: /*#__PURE__*/ _jsx(Edit, {
                        className: "w-4 h-4"
                    }),
                    onClick: ()=>onEdit(course.id),
                    variant: 'default'
                });
            }
            if (isDraft) {
                // 초안일 때: 삭제 버튼
                if (onDelete) {
                    items.push({
                        label: '삭제',
                        icon: /*#__PURE__*/ _jsx(Trash2, {
                            className: "w-4 h-4"
                        }),
                        onClick: ()=>{
                            if (window.confirm('이 초안을 삭제하시겠습니까?')) {
                                onDelete(course.id);
                            }
                        },
                        variant: 'danger'
                    });
                }
            } else {
                // 발행됨일 때: 발행 취소 버튼
                if (onUnpublish) {
                    items.push({
                        label: '발행 취소',
                        icon: /*#__PURE__*/ _jsx(MinusCircle, {
                            className: "w-4 h-4"
                        }),
                        onClick: ()=>{
                            if (window.confirm('이 강의의 발행을 취소하시겠습니까?')) {
                                onUnpublish(course.id);
                            }
                        },
                        variant: 'danger'
                    });
                }
            }
        }
        // 지원 언어가 2개 이상이면 상세 정보 메뉴 추가 (모든 사용자)
        if (languagesForDisplay.length > 1) {
            items.push({
                label: '상세 정보',
                icon: /*#__PURE__*/ _jsx(Info, {
                    className: "w-4 h-4"
                }),
                onClick: onLanguageModalOpen,
                variant: 'default'
            });
        }
        // 신고 메뉴 추가 (모든 사용자, 강사 본인 강의 제외)
        if (onReport && !isInstructor) {
            items.push({
                label: '신고',
                icon: /*#__PURE__*/ _jsx(Flag, {
                    className: "w-4 h-4"
                }),
                onClick: onReportModalOpen,
                variant: 'danger'
            });
        }
        return items.length > 0 ? items : undefined;
    }, [
        isInstructor,
        onEdit,
        onDelete,
        onUnpublish,
        onReport,
        isDraft,
        course.id,
        languagesForDisplay,
        onLanguageModalOpen,
        onReportModalOpen
    ]);
    return /*#__PURE__*/ _jsxs(CardContainer, {
        onClick: handleCardClick,
        children: [
            /*#__PURE__*/ _jsxs(CardContent, {
                children: [
                    author && /*#__PURE__*/ _jsx(HeaderWrapper, {
                        children: /*#__PURE__*/ _jsx(SocialHeader, {
                            author: author,
                            createdAt: createdAt ? new Date(createdAt) : undefined,
                            size: "sm",
                            showTimestamp: true,
                            showActions: !!actionMenuItems,
                            variant: "default",
                            isRetweet: false,
                            showThreadLineTop: false,
                            showThreadLineBottom: false,
                            onAuthorClick: handleAuthorClick,
                            actions: actionMenuItems
                        })
                    }),
                    /*#__PURE__*/ _jsxs(BodyWrapper, {
                        children: [
                            /*#__PURE__*/ _jsxs(TitleWrapper, {
                                children: [
                                    /*#__PURE__*/ _jsx(Title, {
                                        children: title
                                    }),
                                    isInstructor && /*#__PURE__*/ _jsxs(InstructorBadge, {
                                        title: "내가 작성한 강의입니다",
                                        children: [
                                            /*#__PURE__*/ _jsx(InstructorIconWrapper, {
                                                children: /*#__PURE__*/ _jsx(Edit, {})
                                            }),
                                            "내 강의"
                                        ]
                                    }),
                                    isDraft && course.isPublished !== undefined && /*#__PURE__*/ _jsxs(PublishStatusBadge, {
                                        isPublished: course.isPublished,
                                        title: course.isPublished ? "이미 한 번 발행된 강의입니다" : "아직 발행되지 않은 초안입니다",
                                        children: [
                                            /*#__PURE__*/ _jsx(PublishStatusIconWrapper, {
                                                children: course.isPublished ? /*#__PURE__*/ _jsx(CheckCircle2, {}) : /*#__PURE__*/ _jsx(AlertCircle, {})
                                            }),
                                            course.isPublished ? '발행됨' : '미발행'
                                        ]
                                    }),
                                    isEnrolled && progressRate != null && progressRate < 1.0 && /*#__PURE__*/ _jsxs(EnrolledBadge, {
                                        title: "수강 중인 강의입니다",
                                        children: [
                                            /*#__PURE__*/ _jsx(EnrolledIconWrapper, {
                                                children: /*#__PURE__*/ _jsx(CheckCircle, {})
                                            }),
                                            "수강 중"
                                        ]
                                    }),
                                    isCompleted && /*#__PURE__*/ _jsxs(CompletedBadge, {
                                        title: "수강 완료한 강의입니다",
                                        children: [
                                            /*#__PURE__*/ _jsx(CompletedIconWrapper, {
                                                children: /*#__PURE__*/ _jsx(Award, {})
                                            }),
                                            "수강완료"
                                        ]
                                    })
                                ]
                            }),
                            description && /*#__PURE__*/ _jsx(Description, {
                                children: description
                            })
                        ]
                    }),
                    hasThumbnail && /*#__PURE__*/ _jsx(MediaGridWrapper, {
                        children: /*#__PURE__*/ _jsx(ThumbnailImage, {
                            src: thumbnailUrl,
                            alt: title,
                            loading: "lazy"
                        })
                    }),
                    /*#__PURE__*/ _jsxs(FooterRow, {
                        children: [
                            /*#__PURE__*/ _jsx(LeftFooter, {}),
                            /*#__PURE__*/ _jsxs(RightFooter, {
                                children: [
                                    renderPrice(),
                                    renderLanguageSupport(),
                                    (ratingValue !== null || reviewCountValue !== null) && /*#__PURE__*/ _jsxs(RatingWrapper, {
                                        title: `평균 평점: ${ratingValue || 0} / 리뷰: ${reviewCountValue || 0}개`,
                                        children: [
                                            /*#__PURE__*/ _jsx(StarIconWrapper, {
                                                children: /*#__PURE__*/ _jsx(Star, {
                                                    size: 14
                                                })
                                            }),
                                            ratingValue || 0,
                                            reviewCountValue !== null ? ` (${reviewCountValue})` : ''
                                        ]
                                    }),
                                    enrollCountValue !== null && /*#__PURE__*/ _jsxs(EnrollmentWrapper, {
                                        title: `수강생: ${enrollCountValue}명`,
                                        children: [
                                            /*#__PURE__*/ _jsx(UsersIconWrapper, {
                                                children: /*#__PURE__*/ _jsx(Users, {})
                                            }),
                                            enrollCountValue
                                        ]
                                    }),
                                    isEnrolled && progressRate != null && progressRate > 0 && !isCompleted && /*#__PURE__*/ _jsx(ProgressWrapper, {
                                        title: `학습 진행률: ${Math.floor(progressRate * 100)}%`,
                                        children: `${Math.floor(progressRate * 100)}%`
                                    }),
                                    level && /*#__PURE__*/ _jsxs(LevelIndicator, {
                                        title: getLevelDescription(level),
                                        children: [
                                            translateLevel(level),
                                            /*#__PURE__*/ _jsx(LevelInfoIconWrapper, {
                                                children: /*#__PURE__*/ _jsx(Info, {})
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(InfoModal, {
                isOpen: isLanguageModalOpen,
                onClose: onLanguageModalClose,
                title: "지원 언어",
                size: "sm",
                children: /*#__PURE__*/ _jsx("ul", {
                    className: "list-disc list-inside space-y-2",
                    children: languagesForDisplay.map((language)=>/*#__PURE__*/ _jsx("li", {
                            children: getLanguageName(language)
                        }, language))
                })
            }),
            onReport && /*#__PURE__*/ _jsx(ReportDialog, {
                isOpen: isReportModalOpen,
                onClose: onReportModalClose,
                targetType: "course",
                targetId: course.id,
                targetTitle: title,
                onSubmit: handleReportSubmit
            })
        ]
    });
};
export default CourseCard;
